import { Exercise, MUSCLE_GROUP_LABELS } from "../api/types.ts";
import { isNotNullish } from "../util/typeGuards.ts";
import { useDialog } from "./DialogManager.tsx";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { Dropdown } from "./widgets/Dropdown.tsx";
import { DropdownItem } from "./widgets/DropdownItem.tsx";
import { RefObject, useEffect, useRef } from "react";
import { Banner } from "./widgets/Banner.tsx";
import { useDeleteExercise } from "../api/hooks.ts";
import { Button } from "./widgets/Button.tsx";

interface ExerciseLibraryProps {
  isQueried?: boolean;
  untilLimit: number;
  exercises: Exercise[];
  preferredResistanceUnit?: "kg" | "lbs";
  onSelectExercise: (e: any, id: string) => void;
  selectedExercises: string[];
  deselectExercises: () => void;
  actionsRef?: RefObject<HTMLDivElement | null>;
}

const ExerciseLibrary: React.FC<ExerciseLibraryProps> = ({
  isQueried = false,
  untilLimit,
  exercises,
  preferredResistanceUnit,
  onSelectExercise,
  selectedExercises,
  deselectExercises,
  actionsRef,
}) => {
  const { openDialog, setError } = useDialog();
  const deleteExerciseMutation = useDeleteExercise();

  const handleDeleteClick = (exerciseId: string, exerciseName: string) => {
    openDialog("confirm", {
      onConfirm: () =>
        deleteExerciseMutation.mutate(exerciseId, {
          onError: (e) => {
            setError(e);
          },
        }),
      title: "Delete Exercise",
      message: `Are you sure you want to delete "${exerciseName}"? This action cannot be undone.`,
      warningMessage:
        "Routines that reference this exercise will be rendered unusable until they no longer reference the deleted exercise. Workouts created with this exercise are however not affected by this action.",
      confirmText: "Delete",
      variant: "danger",
    });
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      if (actionsRef?.current && actionsRef.current.contains(target)) {
        return;
      }

      deselectExercises();
    };

    if (selectedExercises.length > 0) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionsRef, deselectExercises, selectedExercises]);

  return isQueried && exercises.length === 0 ? (
    <p className="text-gray-500 mb-4">Found no matching exercises.</p>
  ) : exercises.length === 0 ? (
    <div className="text-center py-12">
      <p className="text-gray-500 mb-4">
        There are no exercises in your library. Create an exercise to get
        started!
      </p>
      <Button
        onClick={() => openDialog("create-exercise")}
        disabled={untilLimit !== undefined && untilLimit < 1}
        variant="primary"
      >
        Get started
      </Button>
    </div>
  ) : (
    <div ref={containerRef} className="flex flex-col gap-4 py-4">
      {untilLimit < 2 &&
        (untilLimit < 1 ? (
          <Banner
            visible
            variant="error"
            title="Exercise limit reached"
            body="You will need to delete one of your existing exercises before creating a new one."
          />
        ) : (
          <Banner
            visible
            variant="warning"
            title="Approaching exercise limit"
            body={`You have ${untilLimit} exercise${untilLimit > 1 ? "s" : ""} left to create.`}
          />
        ))}
      {exercises.map((exercise) => {
        const isSelected = selectedExercises.includes(exercise.id);
        return (
          <div
            id={`exercise-${exercise.name.toLowerCase().split(" ").join("-")}`}
            key={exercise.id}
            className={`p-4 rounded-lg shadow-sm hover:shadow-md transition-all bg-white cursor-pointer ${
              isSelected ? "border-2 border-primary" : "border border-gray-200"
            }`}
            onClick={(e) => {
              if (!isSelected && selectedExercises.length > 1) {
                deselectExercises();
              }
              onSelectExercise(e, exercise.id);
            }}
          >
            <div>
              <div className="flex flex-row justify-between">
                <h3 className="text-lg text-gray-800">{exercise.name}</h3>
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    trigger={
                      <button
                        id={`exercise-options-${exercise.name.toLowerCase().split(" ").join("-")}`}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Exercise options"
                      >
                        <HiOutlineDotsVertical
                          className="text-gray-600"
                          size={20}
                        />
                      </button>
                    }
                    alignment="left"
                  >
                    <DropdownItem
                      onClick={() => {
                        handleDeleteClick(exercise.id, exercise.name);
                      }}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </DropdownItem>
                  </Dropdown>
                </div>
              </div>
              <p className="flex flex-col pr-auto">
                {exercise.description && (
                  <span className="text-sm text-gray-600 italic mb-1">
                    {exercise.description}
                  </span>
                )}
                <span className="text-sm text-gray-600">
                  Primary Muscles:{" "}
                  <span className="font-bold">
                    {exercise.primary_muscle_groups
                      .map((mg) => MUSCLE_GROUP_LABELS[mg.muscle_group])
                      .join(", ") || "None"}
                  </span>
                </span>
                {exercise.secondary_muscle_groups.length > 0 && (
                  <span className="text-sm text-gray-600">
                    Secondary Muscles:{" "}
                    <span className="font-bold">
                      {exercise.secondary_muscle_groups
                        .map((mg) => MUSCLE_GROUP_LABELS[mg.muscle_group])
                        .join(", ")}
                    </span>
                  </span>
                )}
                {isNotNullish(preferredResistanceUnit) &&
                  exercise.default_resistance_unit !==
                    preferredResistanceUnit && (
                    <span className="text-sm text-gray-600">
                      Default Resistance Unit:{" "}
                      {exercise.default_resistance_unit}
                    </span>
                  )}
                {exercise.efficiency_factor !== 1 && (
                  <span className="text-sm text-gray-600">
                    Efficiency Factor:{" "}
                    {Number((exercise.efficiency_factor * 100).toFixed(1))}%
                  </span>
                )}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExerciseLibrary;
